import { defineComponent as _defineComponent } from 'vue'
import axios from 'axios'
import { computed, getCurrentInstance, onMounted, ref, toRef, watch } from 'vue'
import VueCookies from 'vue-cookies'
import CardPayment from './CardPayment.vue'
import { useApplePayCheckout } from '@/composables/payment/useApplePayCheckout'
import { useAuth } from '@/composables/useAuth'
import { emitter, emitOpenLoginDialog } from '@/plugins/emitter'
import { isPrivateRelayEmail } from '@/utils/payment/isPrivateRelayEmail'

type HostVm = {
  $deviceLib: { isMobileDevice: boolean }
  $site: { appFlag: string }
  $varuna: { endPoints: Record<string, string> }
  $message: (opts: Record<string, unknown>) => void
}

type CardPaymentExpose = { createToken: () => void }


export default /*#__PURE__*/_defineComponent({
  __name: 'PaymentMethodSelector',
  props: {
  is_simultaneous: { type: Boolean, default: false },
  serviceName: { type: String, default: 'メール便' },
  price: { type: Number, default: 330 },
  is_subscription: { type: Boolean, default: false },
  selectedIndex: { type: Number, default: 1 },
  notificationSetting: { type: String, default: 'on' }
},
  emits: ["paypay-payment"],
  setup(__props, { emit }: { emit: ({
  (e: 'paypay-payment'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props;

/**
 * ヤギプライム等の決済方法セレクタ。Gate A: mitt / useAuth / axios。
 */
const PAYMENT_YAGIPRIME_RESULT = 'payment_yagiprime_result'
const SHOW_PRIVATE_RELAY_WARNING = 'show-private-relay-warning'





const vm = getCurrentInstance()!.proxy as unknown as HostVm
const { isAuthenticated, user, getClient } = useAuth()

const selectedPaymentMethod = ref('card')
const publishableKey = process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY || ''
const isCardFormValid = ref(false)
const isCardButtonDisabled = ref(false)
const cardPayment = ref<CardPaymentExpose | null>(null)

const currentPathEnd = computed(() => {
  const pathParts = window.location.pathname.split('/').filter(Boolean)
  return pathParts.length > 0 ? pathParts[pathParts.length - 1] : ''
})

function currentUid(): string {
  return getClient()?.user?.uid ?? user.value?.uid ?? ''
}

function currentEmail(): string | undefined {
  return getClient()?.user?.email ?? user.value?.email ?? undefined
}

function openLoginDialog() {
  emitOpenLoginDialog()
}

function handlePaymentSuccess(_paymentData: unknown) {
  isCardButtonDisabled.value = false
  emitter.emit(PAYMENT_YAGIPRIME_RESULT, 'success')
  vm.$message({
    message: '決済が完了しました。',
    type: 'success'
  })
}

function handlePaymentError(error: { message?: string } | null) {
  console.error('支払いエラー:', error)
  let errorMessage = '支払いに失敗しました。'
  if (error && error.message) {
    errorMessage = error.message
  }
  vm.$message({
    message: errorMessage,
    type: 'error'
  })
  isCardButtonDisabled.value = false
}

function handleValidationChange(isValid: boolean) {
  isCardFormValid.value = isValid
}

const {
  isApplePayAvailable,
  isDisplayApplePayButton,
  initializeExpressCheckout,
  applePayMount,
  hideApplePayButton
} = useApplePayCheckout({
  publishableKey,
  price: toRef(props, 'price'),
  serviceName: toRef(props, 'serviceName'),
  isSubscription: toRef(props, 'is_subscription'),
  notificationSetting: toRef(props, 'notificationSetting')
})

function handleCardPayment() {
  if (isPrivateRelayEmail(currentEmail())) {
    emitter.emit(SHOW_PRIVATE_RELAY_WARNING, () => {
      executeCardPayment()
    })
    return
  }
  executeCardPayment()
}

function executeCardPayment() {
  isCardButtonDisabled.value = true
  if (selectedPaymentMethod.value === 'card' && isCardFormValid.value) {
    cardPayment.value?.createToken()
  } else {
    alert('カード情報が無効です')
  }
}

function paypayPayment() {
  if (isPrivateRelayEmail(currentEmail())) {
    emitter.emit(SHOW_PRIVATE_RELAY_WARNING, () => {
      executePaypayPayment()
    })
    return
  }
  executePaypayPayment()
}

function executePaypayPayment() {
  if (props.is_subscription) {
    yagiprimePaypay().catch(() => {})
  } else {
    emit('paypay-payment')
  }
}

async function yagiprimePaypay() {
  emitter.emit('loading_on')

  const formData = new FormData()
  formData.append('uuid', currentUid())
  formData.append('subscription_type', String(props.selectedIndex))
  formData.append('notification_setting', props.notificationSetting)
  const routeId = (VueCookies as any).get('_YAGISH_YAGIPRIME_ROUTE_ID')
  formData.append('route', routeId || '')
  formData.append('app_flag', vm.$site.appFlag)
  formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
  formData.append('version', '3')
  formData.append('site_flag', 'r')
  formData.append('redirect_url', window.location.href)
  try {
    const response = await axios.post(
      vm.$varuna.endPoints.paypaySubscriptions,
      formData,
      { timeout: 60000 }
    )
    location.href = response.data.authorization_url
  } catch {
    vm.$message({
      message: '決済失敗しました',
      type: 'error'
    })
    emitter.emit(PAYMENT_YAGIPRIME_RESULT, 'error')
  } finally {
    emitter.emit('loading_off')
  }
}

watch(selectedPaymentMethod, (newVal) => {
  if (newVal === 'applepay') {
    applePayMount().catch(() => {})
  } else {
    hideApplePayButton()
  }
})

onMounted(async () => {
  if (props.is_subscription) {
    await initializeExpressCheckout()
  }
})

return { __sfc: true,PAYMENT_YAGIPRIME_RESULT, SHOW_PRIVATE_RELAY_WARNING, props, emit, vm, isAuthenticated, user, getClient, selectedPaymentMethod, publishableKey, isCardFormValid, isCardButtonDisabled, cardPayment, currentPathEnd, currentUid, currentEmail, openLoginDialog, handlePaymentSuccess, handlePaymentError, handleValidationChange, isApplePayAvailable, isDisplayApplePayButton, initializeExpressCheckout, applePayMount, hideApplePayButton, handleCardPayment, executeCardPayment, paypayPayment, executePaypayPayment, yagiprimePaypay, CardPayment }
}

})